import DungeonConfig from './DungeonConfig.js';
import { ITEM_DEFS, KEY_TIERS, LOOT_TABLES } from '../loot/ItemDefinitions.js';
import { createItemInstance, EXCEPTIONAL_CHANCES } from '../loot/ItemFactory.js';
import DungeonLevel from './DungeonLevel.js';
import DungeonLayout from './DungeonLayout.js';
import DungeonGeometry from './DungeonGeometry.js';
import DungeonTheme from './DungeonTheme.js';
import DungeonPopulator from './DungeonPopulator.js';
import DungeonStreamer from './DungeonStreamer.js';

/**
 * DungeonGenerator - Orchestrator for dungeon generation, level management,
 * and streaming. Generates all levels upfront, manages transitions.
 */
export default class DungeonGenerator {
  /**
   * @param {Object} options
   * @param {World} options.world
   * @param {NPCManager} options.npcManager
   * @param {CombatManager} options.combatManager
   * @param {LootManager} options.lootManager
   * @param {QuestManager} options.questManager
   * @param {HUDManager} options.hudManager
   */
  constructor(options = {}) {
    this.world = options.world;
    this.npcManager = options.npcManager;
    this.combatManager = options.combatManager;
    this.lootManager = options.lootManager;
    this.questManager = options.questManager;
    this.hudManager = options.hudManager;

    /** Set<string> cuboid keys of opened chests */
    this._openedChests = new Set();

    /** Map<number, Array<{id, itemId, x, y, z}>> — per-level saved world items */
    this._levelLoot = new Map();

    /** Set<string> — globally collected item IDs (persists across level transitions) */
    this._pickedUpItems = new Set();

    /** Map<number, Array<{type, completed, ...}>> — per-level saved objective state */
    this._levelObjectives = new Map();

    /** Set<number> — levels that have already shown the "Level Complete" banner */
    this._completedLevels = new Set();

    /** @type {DungeonStreamer} */
    this.streamer = new DungeonStreamer({
      world: this.world,
      npcManager: this.npcManager,
      lootManager: this.lootManager,
      openedChests: this._openedChests,
      pickedUpItems: this._pickedUpItems
    });

    /** @type {Array<DungeonLevel>} Pre-generated levels */
    this.levels = [];

    /** Current level index */
    this.currentLevelIndex = -1;

    /** Dungeon settings */
    this.themeName = 'stone_crypt';
    this.levelCount = 3;
    this.seed = Date.now();

    /** @type {Function|null} Called when dungeon is complete */
    this.onDungeonComplete = null;

    /** @type {Function|null} Called when a level is loaded */
    this.onLevelLoaded = null;

    /** Whether dungeon mode is active */
    this.active = false;

    /** Chest interaction range */
    this.CHEST_RANGE = 2.0;
  }

  /**
   * Generate all levels for a dungeon run.
   * @param {Object} config
   * @param {string} [config.theme='stone_crypt']
   * @param {number} [config.levelCount=3]
   * @param {number} [config.seed]
   */
  generate(config = {}) {
    this.themeName = config.theme || 'stone_crypt';
    this.levelCount = config.levelCount || DungeonConfig.MAX_LEVELS;
    this.seed = config.seed || Date.now();
    this.targetLevel = config.targetLevel || 1;
    this.keyTier = config.keyTier || null;
    this.lootMultiplier = config.lootMultiplier || 1;
    this.levels = [];

    const themes = DungeonTheme.getThemeNames();

    for (let i = 0; i < this.levelCount; i++) {
      const levelConfig = DungeonConfig.getLevelConfig(i, this.targetLevel);

      // Vary theme per level if desired, or use selected theme
      const themeName = config.varyThemes
        ? themes[(themes.indexOf(this.themeName) + i) % themes.length]
        : this.themeName;
      const theme = DungeonTheme.getTheme(themeName);

      // Create layout
      const layout = new DungeonLayout({
        gridWidth: levelConfig.gridWidth,
        gridHeight: levelConfig.gridHeight
      });

      const result = layout.generate({
        roomCount: levelConfig.roomCount,
        seed: this.seed + i * 1000
      });

      // Create level
      const level = new DungeonLevel(i);
      level.grid = result.grid;
      level.gridWidth = layout.width;
      level.gridHeight = layout.height;
      level.rooms = result.rooms;
      level.corridors = result.corridors;
      level.themeName = themeName;

      // Generate cuboid geometry
      level.cuboidDefs = DungeonGeometry.buildCuboids(level, theme);

      // Populate with enemies, items, objectives
      DungeonPopulator.populate(level, levelConfig, layout.rng, this.themeName);

      this.levels.push(level);
    }
  }

  /**
   * Start the dungeon: clear world, load first level.
   */
  async startDungeon() {
    if (this.levels.length === 0) {
      this.generate();
    }

    this.active = true;
    this.currentLevelIndex = -1;
    await this.loadNextLevel();
  }

  /**
   * Load the next level (or complete dungeon if all levels done).
   */
  async loadNextLevel() {
    this.loadLevel(this.currentLevelIndex + 1);
  }

  /**
   * Load a specific level by index.
   * Used for forward progression, backward portal, and late joiners.
   * @param {number} levelIndex
   * @param {Object} [options]
   * @param {string} [options.spawnAt='entry'] - 'entry' or 'exit' spawn location
   */
  loadLevel(levelIndex, options = {}) {
    if (levelIndex < 0 || levelIndex >= this.levels.length) {
      if (levelIndex >= this.levels.length) {
        this._onDungeonComplete();
      }
      return;
    }

    // Save current level's loot and objectives before transitioning
    this._saveLevelState();

    this.currentLevelIndex = levelIndex;
    const level = this.levels[levelIndex];

    // Suppress WorldSync broadcasts during level transition — each peer generates
    // geometry locally from the shared seed, so broadcasting add/remove/clear ops
    // would destroy other peers' dungeon floors and walls.
    const savedOnMutation = this.world.onMutation;
    const savedMgmOnMutation = this.world.materialGroupManager?.onMutation;
    const savedNpcOnMutation = this.npcManager?.onMutation;
    this.world.onMutation = null;
    if (this.world.materialGroupManager) this.world.materialGroupManager.onMutation = null;
    if (this.npcManager) this.npcManager.onMutation = null;

    // Clear previous level geometry and NPCs
    this.streamer.unloadAll();
    this.npcManager.removeAll();
    this.lootManager.clearAll();
    this.world.clear();

    // Set streamer to new level
    this.streamer.setLevel(level);

    // Set sun intensity based on theme
    const theme = DungeonTheme.getTheme(level.themeName);
    if (theme.sunIntensity !== undefined) {
      window.sunIntensity = theme.sunIntensity;
    }

    // Set objectives (then restore any previously-completed state)
    if (this.questManager && level.objectives) {
      this.questManager.setObjectives(level.objectives);
      this._restoreObjectiveState(levelIndex);
    }

    // Update HUD
    if (this.hudManager) {
      this.hudManager.updateDungeonInfo(
        `Level ${this.currentLevelIndex + 1}/${this.levels.length}`,
        DungeonTheme.getTheme(level.themeName).name || level.themeName
      );
    }

    // Force-load chunks around the appropriate spawn point
    const spawnAt = options.spawnAt || 'entry';
    const spawnPoint = (spawnAt === 'exit' && level.exitPoint) ? level.exitPoint : level.entryPoint;
    if (spawnPoint) {
      this.streamer.forceUpdate(spawnPoint.x, spawnPoint.z);
    }

    // Restore mutation callbacks — future edits (if any) can broadcast normally
    this.world.onMutation = savedOnMutation;
    if (this.world.materialGroupManager) this.world.materialGroupManager.onMutation = savedMgmOnMutation;
    if (this.npcManager) this.npcManager.onMutation = savedNpcOnMutation;

    // Trigger world geometry rebuild for collision + navmesh
    if (this.world.onGeometryChanged) {
      this.world.onGeometryChanged();
    }

    if (this.onLevelLoaded) {
      this.onLevelLoaded(level, this.currentLevelIndex, spawnAt);
    }

    // Restore saved loot from a previous visit (enemy drops, chest drops)
    this._restoreLevelLoot(levelIndex);

    // Deferred: re-check kill-based objectives once async NPC spawns complete.
    // NPCs are spawned asynchronously by the streamer, so we must wait before
    // checking kill_all (otherwise no NPCs exist yet → falsely marks complete).
    setTimeout(() => this._recheckKillObjectives(levelIndex), 500);
  }

  /**
   * Check if the player has reached the exit portal.
   * Call this every frame from the game loop.
   * @param {Character} player
   * @returns {boolean} true if player reached exit
   */
  checkExitPortal(player) {
    if (!this.active || !player) return false;

    const level = this.levels[this.currentLevelIndex];
    if (!level || !level.exitPoint) return false;

    const dx = player.x - level.exitPoint.x;
    const dz = player.z - level.exitPoint.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    return dist < 1.5;
  }

  /**
   * Called when dungeon is fully complete (all levels cleared).
   * @private
   */
  _onDungeonComplete() {
    this.active = false;

    if (this.hudManager) {
      this.hudManager.showLevelComplete('DUNGEON COMPLETE!');
    }

    // Show victory screen after the level-complete banner finishes its animation
    if (this.onDungeonComplete) {
      const cb = this.onDungeonComplete;
      setTimeout(() => cb(), 5000);
    }
  }

  /**
   * Clean up dungeon state (called when exiting dungeon mode).
   */
  cleanup() {
    this.active = false;
    this.streamer.unloadAll();
    this.streamer.resetSession(); // Clear killed NPCs tracking (persists across level transitions)
    this.npcManager.removeAll();
    this.lootManager.clearAll();
    this._openedChests.clear();
    this._levelLoot.clear();
    this._pickedUpItems.clear();
    this._levelObjectives.clear();
    this._completedLevels.clear();
    this.levels = [];
    this.currentLevelIndex = -1;
  }

  /**
   * Get the current level.
   * @returns {DungeonLevel|null}
   */
  getCurrentLevel() {
    if (this.currentLevelIndex < 0 || this.currentLevelIndex >= this.levels.length) {
      return null;
    }
    return this.levels[this.currentLevelIndex];
  }

  /**
   * Try to open a nearby chest. Called when player presses E.
   * @param {Character} player
   * @returns {boolean} true if a chest was opened
   */
  tryOpenChest(player) {
    if (!this.active || !player) return false;

    const lvl = this.currentLevelIndex;

    // Search loaded cuboids for nearby chest
    for (const [key, cuboids] of this.streamer._chunkCuboids) {
      for (const cuboid of cuboids) {
        if (cuboid._dungeonRole !== 'chest') continue;

        // Level-prefixed key prevents cross-level collisions
        const cKey = `L${lvl}_${cuboid.getKey()}`;
        if (this._openedChests.has(cKey)) continue;

        const cx = cuboid.x + cuboid.width / 2;
        const cz = cuboid.z + cuboid.depth / 2;
        const dx = player.x - cx;
        const dz = player.z - cz;
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist < this.CHEST_RANGE) {
          this._openChest(cuboid, cKey);
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Open a chest: change material, spawn loot, notify quest.
   * @private
   * @param {Object} cuboid
   * @param {string} cKey - Level-prefixed key (e.g. "L0_5,0,8,1,1,1")
   */
  _openChest(cuboid, cKey) {
    this._openedChests.add(cKey);

    // Change chest appearance (darken to indicate opened)
    if (cuboid.element) {
      const faces = cuboid.element.querySelectorAll('.face');
      for (const face of faces) {
        face.style.filter = 'brightness(0.4)';
      }
    }

    // Spawn loot around chest
    const spawnedLoot = [];
    if (this.lootManager) {
      const cx = cuboid.x + cuboid.width / 2;
      const cz = cuboid.z + cuboid.depth / 2;
      const chestY = cuboid.y + cuboid.height;

      // Determine chest loot based on keyed dungeon tier or default
      const chestItemLevel = this.targetLevel + (this.currentLevelIndex || 0);
      const exceptionalChance = this.keyTier
        ? EXCEPTIONAL_CHANCES[this.keyTier] || EXCEPTIONAL_CHANCES.boss
        : EXCEPTIONAL_CHANCES.elite;

      if (this.keyTier && KEY_TIERS[this.keyTier]) {
        // Keyed dungeon: use tier-specific loot table
        const tierTable = LOOT_TABLES[KEY_TIERS[this.keyTier].lootTable] || LOOT_TABLES.elite;
        const lootMul = this.lootMultiplier || 1;
        for (const entry of tierTable) {
          if (Math.random() <= Math.min(1, entry.chance * lootMul)) {
            const instanceData = createItemInstance(entry.itemId, { itemLevel: chestItemLevel, exceptionalChance });
            const item = this.lootManager.spawnItem(entry.itemId, cx, chestY, cz, instanceData);
            if (item) spawnedLoot.push(item);
          }
        }
      } else {
        // Default chest: always a bronze-key + 1-2 random items
        const key = this.lootManager.spawnItem('bronze-key', cx, chestY, cz);
        if (key) spawnedLoot.push(key);
        const extraItems = ['health-potion', 'iron-sword', 'leather-armor'];
        const extraCount = 1 + Math.floor(Math.random() * 2);
        for (let i = 0; i < extraCount; i++) {
          const id = extraItems[Math.floor(Math.random() * extraItems.length)];
          const instanceData = createItemInstance(id, { itemLevel: chestItemLevel, exceptionalChance });
          const item = this.lootManager.spawnItem(id, cx, chestY, cz, instanceData);
          if (item) spawnedLoot.push(item);
        }
      }
    }

    // In multiplayer dungeon: broadcast chest open + spawned loot to all peers.
    // Send the raw cuboid key (not level-prefixed) so receivers can match by cuboid.getKey().
    const dim = window.game?.dungeonInstanceManager;
    if (dim?._isInDungeon) {
      dim.broadcastChestOpen(cuboid.getKey(), this.currentLevelIndex, spawnedLoot);
    }

    // Notify quest system (for find_item objectives)
    if (this.questManager) {
      this.questManager.onItemAcquired('dungeon-key');
      this.questManager.onItemAcquired('bronze-key');
    }
  }

  /**
   * Check if player fell off the map.
   * @param {Character} player
   * @returns {boolean}
   */
  checkFallOffMap(player) {
    if (!this.active || !player) return false;
    return player.y < -20;
  }

  /**
   * Check if the player has reached a back portal (level 2+ only).
   * Back portal is located near the entry point of the current level.
   * @param {Character} player
   * @returns {boolean}
   */
  checkBackPortal(player) {
    if (!this.active || !player) return false;
    if (this.currentLevelIndex <= 0) return false; // No back portal on level 1

    const level = this.levels[this.currentLevelIndex];
    if (!level || !level.backPortalPoint) return false;

    const dx = player.x - level.backPortalPoint.x;
    const dz = player.z - level.backPortalPoint.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    return dist < 1.5;
  }

  /**
   * Update the streamer each frame.
   * @param {number} deltaTime
   * @param {Character} player
   */
  update(deltaTime, player) {
    if (!this.active) return;
    this.streamer.update(deltaTime, player);
  }

  /**
   * Update streamer considering all player positions (multiplayer).
   * @param {number} deltaTime
   * @param {Character} localPlayer
   * @param {Map} remotePlayers - from PlayerSync.remotePlayers
   * @param {number} myLevel - current level index of local player
   */
  updateMultiplayer(deltaTime, localPlayer, remotePlayers, myLevel) {
    if (!this.active) return;
    this.streamer.updateMultiplayer(deltaTime, localPlayer, remotePlayers, myLevel);
  }

  /**
   * Apply a delta from authority (for late joiners).
   * Marks NPCs as killed and chests as opened.
   * @param {Set<string>} killedNPCs
   * @param {Set<string>} openedChests
   */
  applyDungeonDelta(killedNPCs, openedChests, pickedUpItems) {
    if (killedNPCs) {
      this.streamer.setKilledNPCs(killedNPCs);
    }
    if (openedChests) {
      for (const key of openedChests) {
        this._openedChests.add(key);
      }
    }
    if (pickedUpItems) {
      for (const id of pickedUpItems) {
        this._pickedUpItems.add(id);
      }
    }
  }

  // ────────────────────────────────────────────────────────
  //  LEVEL STATE PERSISTENCE
  // ────────────────────────────────────────────────────────

  /**
   * Save current level's loot and objective state before transitioning.
   * @private
   */
  _saveLevelState() {
    const idx = this.currentLevelIndex;
    if (idx < 0) return;

    // Save world loot (enemy drops, chest drops still on the ground)
    const lootSnapshot = [];
    for (const [id, item] of this.lootManager.worldItems) {
      if (!item.collected) {
        lootSnapshot.push({ id: item.id, itemId: item.itemId, x: item.x, y: item.baseY, z: item.z });
      }
    }
    this._levelLoot.set(idx, lootSnapshot);

    // Save objective completion state
    if (this.questManager?.objectives?.length) {
      this._levelObjectives.set(idx, this.questManager.objectives.map(o => ({
        type: o.type, completed: o.completed,
        itemId: o.itemId, target: o.target, count: o.count
      })));
    }
  }

  /**
   * Restore saved loot for a level (skipping already-picked-up items).
   * @private
   */
  _restoreLevelLoot(levelIndex) {
    const saved = this._levelLoot.get(levelIndex);
    if (!saved) return;
    for (const item of saved) {
      if (this._pickedUpItems.has(item.id)) continue;
      this.lootManager.spawnNetworkItem(item.id, item.itemId, item.x, item.y, item.z);
    }
  }

  /**
   * Restore saved objective completion state for a level.
   * Called after setObjectives() to re-mark previously completed objectives.
   * @private
   */
  _restoreObjectiveState(levelIndex) {
    const saved = this._levelObjectives.get(levelIndex);
    if (!saved || !this.questManager?.objectives) return;
    let changed = false;
    for (const obj of this.questManager.objectives) {
      const match = saved.find(s => s.type === obj.type
        && (s.itemId === obj.itemId || (!s.itemId && !obj.itemId))
        && (s.target === obj.target || (!s.target && !obj.target)));
      if (match?.completed && !obj.completed) {
        obj.completed = true;
        changed = true;
      }
    }
    this.questManager._updateHUD();
    if (changed) this.questManager._checkCompletion();
  }

  /**
   * Re-check kill-based objectives after chunks are loaded.
   * Called after forceUpdate() so the world has NPCs — killed ones were excluded.
   * This catches kill_all and kill_count objectives completed by other peers.
   * @private
   */
  _recheckKillObjectives(levelIndex) {
    if (!this.questManager?.objectives) return;
    const dim = window.game?.dungeonInstanceManager;
    let changed = false;

    for (const obj of this.questManager.objectives) {
      if (obj.completed) continue;

      if (obj.type === 'kill_all') {
        // Check if all hostile NPCs are dead (killed ones weren't spawned)
        let anyAlive = false;
        for (const char of this.world.characters.values()) {
          if (char.isNPC && char.combatStats && !char.combatStats.isDead && char.combatStats.isHostile) {
            anyAlive = true;
            break;
          }
        }
        if (!anyAlive) { obj.completed = true; changed = true; }
      } else if (obj.type === 'kill_count') {
        // Replay remote kill count from DIM
        const remoteKills = dim?._remoteKillCounts?.get(levelIndex) || 0;
        if (remoteKills > 0) {
          // Add remote kills to quest manager's total
          this.questManager._totalKills += remoteKills;
          const count = obj.target
            ? (this.questManager._killCounts.get(obj.target) || 0)
            : this.questManager._totalKills;
          if (count >= (obj.count || 1)) { obj.completed = true; changed = true; }
        }
      }
    }

    if (changed) {
      this.questManager._updateHUD();
      this.questManager._checkCompletion();
    }
  }

  /**
   * Track a collected item globally. Called on local pickup and remote pickup broadcasts.
   * Prevents the item from reappearing when revisiting a level.
   * @param {string} netId - The item's unique ID
   */
  onLootPickedUp(netId) {
    this._pickedUpItems.add(netId);
    // Also remove from saved level loot (cross-level pickup by another peer)
    for (const [lvl, items] of this._levelLoot) {
      const idx = items.findIndex(it => it.id === netId);
      if (idx !== -1) { items.splice(idx, 1); break; }
    }
  }
}
